import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";
import { HTMLAttributes, ReactNode, forwardRef } from "react";

type Props = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  title?: string;
  horizontal?: boolean;
  value?: ReactNode;
  valueClassName?: string;
};

/**
 * A reusable card component for displaying content with a consistent styling.
 * Can be used for metrics, information panels, or any content that needs to be displayed in a card format.
 */
export const ContentCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      children,
      title,
      horizontal = false,
      value,
      valueClassName,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "rounded-2xl border border-neutral-500 bg-white p-3",
        horizontal
          ? "flex flex-col gap-2 sm:flex sm:flex-row sm:justify-between"
          : "flex flex-col",
        className,
      )}
      {...props}
    >
      {(title || value) && (
        <div className="space-y-0.5">
          {title && (
            <Typography size="xs" letterSpacing="wide" color="neutral">
              {title}
            </Typography>
          )}
          {value && (
            <Typography size="2xl" weight="semibold" className={valueClassName}>
              {value}
            </Typography>
          )}
        </div>
      )}

      {children}
    </div>
  ),
);
ContentCard.displayName = "ContentCard";
